import { useEffect, useMemo, useRef } from 'react';

import { batch } from '@legendapp/state';
import { use$ } from '@legendapp/state/react';
import type { ZodModelElement } from '@visualizer/schemas';
import { zodI18n } from '@visualizer/schemas';
import { z } from 'zod';

import { useFeatureAppConfig } from './use-feature-app-config';

import { createBffFetchOptions } from '../utils/create-bff-fetch-options';
import { warning } from '../utils/errors';
// import { useLogger } from '../utils/logging';
import { makeCancelable } from '../utils/make-cancelable';

import { ContentId, TrackingEventName } from '../store/stores/tracking/types';

import { fetchBffData } from '../bff-utils/fetch-bff-data';
import { useFeatureAppContent } from '../context/feature-app-content';
// import { useFeatureAppContent } from '../context/feature-app-content';
import { useFeatureAppEnvironment } from '../context/feature-app-env.context';
import { rejectLoadingPromise, resolveLoadingPromise } from '../feature-app';
import type { BFFAssetResponse, I18nResponse } from '../store';
import { containerStore, emissionStoreActions, interiorStoreActions, rootStore, rootStoreActions } from '../store';
import { trackingStore } from '../store/stores/tracking-store';

const bffAssetResponseSchema: z.ZodType<BFFAssetResponse> = z.custom<BFFAssetResponse>();

const translationsSchema = zodI18n.shape.translations;

const i18nSchema = z
	.object({
		api_version: z.string().nullish(),
		application: z.string().nullish(),
		language: z.string().nullish(),
		tenant: z.string().nullish(),
		translations: translationsSchema
	})
	.transform(
		(value): I18nResponse => ({
			api_version: value.api_version ?? '',
			application: value.application ?? '',
			language: value.language ?? '',
			tenant: value.tenant ?? '',
			translations: translationsSchema.parse(value.translations ?? {})
		})
	);

const mediaDataPayloadSchema = z.object({
	assets: z.record(bffAssetResponseSchema),
	i18n: i18nSchema,
	warnings: z
		.array(
			z.object({
				message: z.string(),
				code: z.string().optional(),
				context: z.string().optional()
			})
		)
		.optional(),
	errors: z
		.array(
			z.object({
				message: z.string(),
				level: z.string().optional(),
				reason: z.string().optional()
			})
		)
		.optional()
});

type NodeIdSearchInput = {
	readonly carlineId: string;
	readonly salesgroupId: string;
	readonly trimName: string;
};

function normalizeKey(value: string): string {
	return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function findNodeIdFromModelStructure(
	modelElements: ZodModelElement[],
	{ carlineId, salesgroupId, trimName }: NodeIdSearchInput
): string | undefined {
	const normalizedTrimName = normalizeKey(trimName);
	let fallbackMatch: string | undefined;

	const visit = (element: ZodModelElement): string | undefined => {
		if (element.type === 'trim' && 'key' in element && element.key) {
			const { carlineId: elementCarlineId, salesgroupId: elementSalesgroupId, trimId } = element.key;
			const normalizedTrimId = trimId ? normalizeKey(trimId) : '';

			if (
				elementCarlineId === carlineId &&
				elementSalesgroupId === salesgroupId &&
				normalizedTrimId === normalizedTrimName
			) {
				return element.nodeId;
			}

			if (!fallbackMatch && elementCarlineId === carlineId && normalizedTrimId === normalizedTrimName) {
				fallbackMatch = element.nodeId;
			}
		}

		if ('children' in element && Array.isArray(element.children)) {
			for (const child of element.children) {
				const match = visit(child);
				if (match) {
					return match;
				}
			}
		}

		return undefined;
	};

	for (const element of modelElements) {
		const match = visit(element);
		if (match) {
			return match;
		}
	}

	return fallbackMatch;
}

// @TODO fix types
// @TODO check mofa for onLoad arg
export function useMediaData() {
	const { baseUrl, featureServices } = useFeatureAppEnvironment();
	const config = useFeatureAppConfig();

	const fetchCancelFunction = useRef<() => void>(() => undefined);
	const loadObserverRef = useRef<IntersectionObserver | null>(null);
	const hasTrackedVisualizerLoad = useRef(false);
	const visualizerContainer = use$(containerStore.container) as HTMLElement | null;
	const isBffLoaded = use$(rootStore.isLoaded.bff);
	const trackVisualizerEvent = trackingStore.track;

	// @TODO normal state
	// const [{ modelOverviewResult }, dispatch] = useUniversalState();
	// const logger = useLogger();

	const {
		gmcBundleId,
		gmcBundleVersion,
		sortTrims,
		sortColors,
		sortRims,
		sortInteriors,
		excludeTrims,
		excludeColors,
		excludeRims,
		excludeInteriors,
		excludeColorsByTrim,
		excludeRimsByTrim
	} = useFeatureAppContent();

	const fetchOptions = useMemo(
		() =>
			createBffFetchOptions({
				baseUrl,
				config,
				featureServices
			}),
		[featureServices, config, baseUrl]
	);

	useEffect(() => {
		fetchCancelFunction.current();
		hasTrackedVisualizerLoad.current = false;

		const sortList = JSON.stringify({
			sortTrims,
			sortColors,
			sortRims,
			sortInteriors
		});

		const excludeList = JSON.stringify({
			excludeTrims,
			excludeColors,
			excludeRims,
			excludeInteriors,
			excludeColorsByTrim,
			excludeRimsByTrim
		});

		// @TODO bad usage of mock
		const { promise, cancel } = makeCancelable(
			fetchBffData('/media-data', {
				...fetchOptions,
				fetchPrices: false,
				fetchWltp: true,
				bundleId: gmcBundleId,
				bundleVersion: gmcBundleVersion,
				sortList,
				excludeList
			})
		);

		fetchCancelFunction.current = cancel;

		promise
			.then(data => {
				const payload = mediaDataPayloadSchema.parse(data.payload);

				if (Object.keys(payload.assets).length === 0) {
					batch(() => {
						rootStoreActions.setBffData(payload);
						rootStoreActions.setTranslations(payload.i18n.translations);
					});
					warning('media-data returned no assets; skipping visualizer render');
					resolveLoadingPromise();
					return;
				}

				let firstModelValue: BFFAssetResponse | undefined;
				let firstModelKey: string | undefined;

				batch(() => {
					rootStoreActions.setBffData(payload);

					const translations = payload.i18n.translations;
					rootStoreActions.setTranslations(translations);

					const assetEntries = Object.entries(payload.assets);
					const firstModelEntry = assetEntries[0];
					if (!firstModelEntry) {
						throw new Error('Unable to retrieve first model entry');
					}
					[firstModelKey, firstModelValue] = firstModelEntry;
					rootStoreActions.setModelData([firstModelKey, firstModelValue]);

					const exteriorEntries = Object.entries(firstModelValue.exterior || {});
					if (exteriorEntries.length === 0) {
						throw new Error(`No exterior assets for model ${firstModelKey}`);
					}
					const firstVariantEntry = exteriorEntries[0];
					if (!firstVariantEntry) {
						throw new Error(`Unable to retrieve first variant entry for ${firstModelKey}`);
					}
					const [firstVariantKey, firstVariantValue] = firstVariantEntry;

					rootStoreActions.setVariantData([firstVariantKey, firstVariantValue.assets, firstVariantValue.baseUrl]);

					if (firstModelValue.interior && Object.keys(firstModelValue.interior).length > 0) {
						const interiorEntries = Object.entries(firstModelValue.interior);
						if (interiorEntries.length === 0) {
							throw new Error(`No interior assets for model ${firstModelKey}`);
						}
						const firstInteriorEntry = interiorEntries[0];
						if (!firstInteriorEntry) {
							throw new Error(`Unable to retrieve first interior entry for ${firstModelKey}`);
						}
						const [firstInteriorKey, firstInteriorValue] = firstInteriorEntry;
						interiorStoreActions.setInteriorData([firstInteriorKey, firstInteriorValue.assets]);
					}

					emissionStoreActions.setEmissionData(firstModelValue.trimLayerDetails || undefined);
				});

				if (!firstModelValue) {
					throw new Error('Unable to retrieve current model value');
				}

				const { carlineCode, salesgroupCode, trimName } = firstModelValue.vehicleConfiguration || {};

				rootStoreActions.setCurrentModelNodeId(undefined);

				if (carlineCode && salesgroupCode && trimName) {
					fetchBffData('/model-structure', fetchOptions)
						.then(modelStructure => {
							const nodeId = findNodeIdFromModelStructure(modelStructure.payload.modelElements, {
								carlineId: carlineCode,
								salesgroupId: salesgroupCode,
								trimName
							});

							if (nodeId) {
								rootStoreActions.setCurrentModelNodeId(nodeId);
							} else {
								console.warn('[model-structure] no matching nodeId found for current model:', {
									carlineId: carlineCode,
									salesgroupId: salesgroupCode,
									trimName
								});
							}
						})
						.catch(error => {
							console.warn('[model-structure] failed to fetch model structure:', error);
						});
				} else {
					console.warn('[model-structure] missing vehicleConfiguration data:', {
						carlineId: carlineCode,
						salesgroupId: salesgroupCode,
						trimName
					});
				}

				resolveLoadingPromise();
			})
			.catch(err => rejectLoadingPromise(err));
	}, []);

	useEffect(() => {
		if (!isBffLoaded || !visualizerContainer || hasTrackedVisualizerLoad.current) {
			return;
		}

		if (loadObserverRef.current) {
			loadObserverRef.current.disconnect();
			loadObserverRef.current = null;
		}

		const observer = new IntersectionObserver(
			entries => {
				entries.forEach(({ isIntersecting }) => {
					if (!hasTrackedVisualizerLoad.current && isIntersecting) {
						trackVisualizerEvent(TrackingEventName.VISUALIZER_LOAD, {
							contentId: ContentId.IMAGE_BASED_VIEW
						});
						hasTrackedVisualizerLoad.current = true;
						observer.disconnect();
						loadObserverRef.current = null;
					}
				});
			},
			{
				threshold: 0.4,
				rootMargin: '0px'
			}
		);

		loadObserverRef.current = observer;
		observer.observe(visualizerContainer);

		return () => {
			observer.disconnect();
			if (loadObserverRef.current === observer) {
				loadObserverRef.current = null;
			}
		};
	}, [isBffLoaded, trackVisualizerEvent, visualizerContainer]);
}
